import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Route, Routes, Navigate, Link, useLocation, useParams } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';
import Sidebar from './components/layout/Sidebar';
import { getLocalStorage, removeLocalStorage, setLocalStorage, Utils, userDataToStorageString, getSafeRedirectPath, hasCompletedOnboarding, isStaffUser, resolveStaffPostLoginPath, fetchWithAuth, can, canAny, getMarketingUrl, authJsonHeaders } from "./utils";
import Dashboard from './components/dashboard/Dashboard';
import PushNotification from './components/push/PushNotification';
import PushTemplatesList from './components/push/PushTemplatesList';
import Settings from './components/organization/Settings';
import { AuthProvider } from './components/auth/AuthContext';
// Commented out old signup flow - replaced with new user registration
// import NewSignUp from './components/auth/NewSignUp';
import NewSignIn from './components/auth/NewSignIn';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import Plan from './components/billing/Plan';
import Roles from './components/organization/Roles';
import Staff from './components/organization/Staff';
import Payment from './components/billing/Payment';
import PlanChangeRequest from './components/billing/PlanChangeRequest';
import Billing from './components/billing/Billing';
import Platforms from './components/applications/Platforms';
import OnboardSteps from './components/auth/OnboardSteps';
import NewUserSignup from './components/auth/NewUserSignup';
import Subscriptions from './components/users/Subscriptions';
import Applications from './components/applications/Applications';
import Organizations from './components/organization/Organizations';
import SiteCheck from './components/applications/SiteCheck';
import NewApp from './components/applications/NewApp';
import NewOrganization from './components/organization/NewOrganization';
import AppSetting from './components/applications/AppSetting';
import TestModal from './components/push/TestModal';
import TestSubscription from './components/push/TestSubscription';
import PushSetting from './components/push/PushSetting';
import ApiKeys from './components/applications/ApiKeys';
import AppDefaultSetting from './components/applications/AppDefaultSetting';
import WhatsAppSettings from './components/whatsapp/WhatsAppSettings';
import {
  WhatsAppIndexRedirect,
  WhatsAppLegacyRedirect,
  WhatsAppCampaignReport,
} from './components/whatsapp/WhatsAppRoutes';
import AppSupportTicketDetails from './components/app-support/AppSupportTicketDetails';
import AppSupportTicket from './components/app-support/AppSupportTicket';
import AppSupportTeamMembers from './components/app-support/AppSupportTeamMembers';
import AppSupportCustomers from './components/app-support/AppSupportCustomers';
import AppSupportGroups from './components/app-support/AppSupportGroups';
import AppSupportFields from './components/app-support/AppSupportFields';
import Users from './components/users/Users';
import UserProfile from './components/users/UserProfile';
import SubscriptionProfile from './components/users/SubscriptionProfile';
import Segments from './components/users/Segments';
import CampaignDashboard from './components/engage/CampaignDashboard';
import CampaignDetail from './components/engage/CampaignDetail';
import OneLinkList from './components/engage/OneLinkList';
import EventList from './components/engage/EventList';
import FunnelAnalysis from './components/engage/FunnelAnalysis'
import FunnelList from './components/engage/FunnelList';
import AutomationsHome from './components/automations/AutomationsHome';
import AutomationBuilder from './components/automations/AutomationBuilder';
import CollaboratePartner from './components/collaborate/CollaboratePartner';
import CollaboratePartnerDetail from './components/collaborate/CollaboratePartnerDetail';
import SentMessages from './components/push/SentMessages';
import ScheduledMessages from './components/push/ScheduledMessages';
import SupportChat from './components/app-support/SupportChat';

// Admin imports
import {
  AdminLogin,
  AdminForgotPassword,
  AdminResetPassword,
  AdminLayout,
  AdminDashboard,
  AdminUsers,
  AdminApplications,
  AdminAppUsers,
  AdminPlans,
  AdminPages,
  AdminSettings,
  AdminSupport,
  AdminRegistrationRequests,
  AdminBilling,
  AdminRoles,
  AdminPartners
} from './components/admin';


/** Old `/support/assignees` URL → `/support/team-members`. */
function RedirectSupportAssigneesToTeamMembers() {
  const { appId } = useParams();
  if (!appId) return <Navigate to="/apps" replace />;
  return <Navigate to={`/apps/${appId}/support/team-members`} replace />;
}

// Re-check the pending bill at most this often while the user keeps navigating (ms).
const PENDING_BILL_REFRESH_MS = 60 * 1000;
// Pages where the banner would only repeat what is already on screen.
const PENDING_BILL_HIDDEN_PATHS = ['/billing', '/payment'];

/**
 * Full-width notice at the top of every dashboard page while the account has a pending bill.
 * Text, link and type come from the backend (`pending_bill_notice` from /user/billing/pending-notice).
 */
function PendingBillBanner() {
  const location = useLocation();
  const [notice, setNotice] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const lastFetchRef = useRef(0);

  const load = useCallback(async () => {
    lastFetchRef.current = Date.now();
    try {
      const res = await fetch(`${Utils.baseurl}user/billing/pending-notice`, {
        method: 'POST',
        headers: authJsonHeaders(),
        body: '{}',
      });
      const r = await res.json();
      setNotice(r?.status ? r.data?.pending_bill_notice || null : null);
    } catch {
      // Best-effort banner; never block the page.
    }
  }, []);

  useEffect(() => {
    if (Date.now() - lastFetchRef.current >= PENDING_BILL_REFRESH_MS) load();
  }, [location.pathname, load]);

  const path = location.pathname || '';
  if (!notice || dismissed || PENDING_BILL_HIDDEN_PATHS.some((p) => path === p || path.startsWith(`${p}/`))) return null;

  const isOverdue = notice.type === 'overdue';
  return (
    <div className={`pending-bill-banner ${isOverdue ? 'pending-bill-banner--overdue' : ''}`} role="alert">
      <span className="pending-bill-banner__text">{notice.message}</span>
      {notice.link && can('billing.view') ? (
        <Link className="pending-bill-banner__link" to={notice.link}>
          {notice.link_text || 'Go to billing'}
        </Link>
      ) : null}
      <button type="button" className="pending-bill-banner__close" aria-label="Dismiss" onClick={() => setDismissed(true)}>
        ×
      </button>
    </div>
  );
}

/** Already logged-in user hitting auth pages → redirect to dashboard/ticket or ?redirect= target. */
function RedirectAuthenticatedUser() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const redirectPath = getSafeRedirectPath(params);
  if (redirectPath) {
    return <Navigate to={redirectPath} replace />;
  }
  try {
    const userData = JSON.parse(getLocalStorage(Utils.storageKeys.userData) || '{}');
    if (!hasCompletedOnboarding(userData) && !isStaffUser(userData)) {
      return <Navigate to="/steps" replace />;
    }
    if (isStaffUser(userData)) {
      return <Navigate to={resolveStaffPostLoginPath(userData)} replace />;
    }
  } catch (_) { }
  return <Navigate to="/apps" replace />;
}

function RedirectUnauthenticatedToSignIn() {
  const location = useLocation();
  if (location.pathname === '/' || location.pathname === '') {
    return <Navigate to="/signIn" replace />;
  }
  const target = `${location.pathname}${location.search}`;
  const signInPath = `/signIn?redirect=${encodeURIComponent(target)}`;
  return <Navigate to={signInPath} replace />;
}

/** Terms / privacy / docs live on the marketing site (Next.js). */
function RedirectToMarketing({ path }) {
  useEffect(() => {
    window.location.replace(getMarketingUrl(path));
  }, [path]);
  return null;
}

/** Route gate: allow if user has any of the listed permissions. */
function RequirePermission({ anyOf, children, fallback = "/apps" }) {
  const keys = Array.isArray(anyOf) ? anyOf : [anyOf];
  const ok = keys.length === 1 ? can(keys[0]) : canAny(keys);
  if (!ok) return <Navigate to={fallback} replace />;
  return children;
}

const App = () => {
  const location = useLocation();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [token, setToken] = useState(() => getLocalStorage(Utils.storageKeys.token));
  const [onboard, setOnboard] = useState(() => getLocalStorage(Utils.storageKeys.onboard) === 'true');
  const [dashboardClass, setDashboardClass] = useState("dashboard_page")
  const [currentSidebar, setCurrentSidebar] = useState(() => getLocalStorage(Utils.storageKeys.currentSidebar) || 'defaultSidebar');
  const [appId, setAppId] = useState(() => getLocalStorage(Utils.storageKeys.activeApp) || 0)
  const [thirdSidebar, setThirdSidebar] = useState(0);
  const [authUserVersion, setAuthUserVersion] = useState(0);

  // Admin state
  const [adminToken, setAdminToken] = useState(() => getLocalStorage(Utils.storageKeys.adminToken));
  // Function to handle setting the token
  function handleSaveToken(token, userData, refreshToken) {
    setToken(token);
    setLocalStorage(Utils.storageKeys.token, token);  // Save token in localStorage (for persistence after page reload)
    if (refreshToken) setLocalStorage(Utils.storageKeys.refreshToken, refreshToken);

    setLocalStorage(Utils.storageKeys.userData, userDataToStorageString(userData));
    setAuthUserVersion((v) => v + 1);
  }


  const handleSetOnboard = () => {
    setOnboard(true);
    setLocalStorage(Utils.storageKeys.onboard, 'true');  // Save onboard status in localStorage
  };

  // Function to handle removing the token (on sign out)
  const handleRemoveToken = () => {
    setToken(null);
    setOnboard(false);
    removeLocalStorage(Utils.storageKeys.application);
    removeLocalStorage(Utils.storageKeys.token);  // Remove token from localStorage
    removeLocalStorage(Utils.storageKeys.userData);
    removeLocalStorage(Utils.storageKeys.onboard);
    removeLocalStorage(Utils.storageKeys.refreshToken);
    setAuthUserVersion((v) => v + 1);
  };

  // Refresh role + permission keys into localStorage (needed for can() after deploy / role changes)
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const existing = JSON.parse(getLocalStorage(Utils.storageKeys.userData) || "{}");
        if (!existing?.id) return;
        const res = await fetchWithAuth(`${Utils.baseurl}user/getbyid`, {
          method: "POST",
          body: JSON.stringify({ userId: existing.id }),
        });
        const result = await res.json().catch(() => ({}));
        if (cancelled || !result?.status || !result.data) return;
        setLocalStorage(
          Utils.storageKeys.userData,
          userDataToStorageString({
            ...existing,
            ...result.data,
          })
        );
        setAuthUserVersion((v) => v + 1);
      } catch (_) {
        /* ignore — can() falls back to stored userData */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    setMobileSidebarOpen(false);

    const appUrlMatch = location.pathname.match(/^\/apps\/([^/]+)/);
    const urlAppId = appUrlMatch ? appUrlMatch[1] : null;
    const isAppInnerRoute = Boolean(urlAppId && urlAppId !== "new");
    // New-app wizard (/apps/new, configure) manages its own sidebar / thirdSidebar.
    const isAppWizardRoute = location.pathname.startsWith("/apps/new");

    if (isAppInnerRoute) {
      // Platform Settings sets thirdSidebar=1 (hides menu); restore on normal app routes / back nav
      if (currentSidebar !== "dashboardSidebar" || Number(thirdSidebar) !== 0) {
        handleChangeSidebar("dashboardSidebar", 0);
      }
      if (String(appId) !== urlAppId) {
        handleSetAppId(urlAppId);
      }
    } else if (!isAppWizardRoute) {
      // Account pages (Staff, Roles, Apps list, Plan, …) must not keep the in-app menu
      if (currentSidebar !== "defaultSidebar" || Number(thirdSidebar) !== 0) {
        handleChangeSidebar("defaultSidebar", 0);
      }
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (mobileSidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileSidebarOpen]);

  // Menu items and corresponding components
  const menuItems = [
    { name: 'Dashboard', route: 'dashboard', component: Dashboard },
    { name: 'Push Notification', route: 'apps/:appId/push-notification', component: PushNotification },
    { name: 'Platforms', route: '/apps/:appId/platform', component: Platforms },
    { name: 'Subscriptions', route: 'apps/:appId/subscriptions', component: Subscriptions },
    { name: 'TestSubscription', route: 'apps/:appId/testsubscription', component: TestSubscription },
    { name: 'Settings', route: 'settings', component: Settings },
  ];

  function handleDashboardClass(expand) {
    if (expand) {
      setDashboardClass("dashboard_page collapsed_menu")
    } else {
      setDashboardClass("dashboard_page")
    }

  }

  // Function to change sidebar state and store it in localStorage
  const handleChangeSidebar = (newSidebar, thirdSidebar) => {
    setThirdSidebar(thirdSidebar)
    setCurrentSidebar(newSidebar);
    setLocalStorage(Utils.storageKeys.currentSidebar, newSidebar); // Store sidebar state
    setLocalStorage(Utils.storageKeys.thirdSidebar, String(thirdSidebar));
  };

  function handleSetAppId(id) {
    setLocalStorage(Utils.storageKeys.activeApp, String(id))
    setAppId(id)
  }





  // Admin handlers
  const handleAdminLogin = (token, data) => {
    setAdminToken(token);
  };

  const handleAdminLogout = () => {
    setAdminToken(null);
  };

  // Check if current path is admin
  const isAdminPath = window.location.pathname.startsWith('/admin');

  // Render Admin Panel
  if (isAdminPath) {
    return (
      <AuthProvider>
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
        <Routes>
          <Route path="/admin/login" element={
            adminToken ? <Navigate to="/admin/dashboard" /> : <AdminLogin onLogin={handleAdminLogin} />
          } />
          <Route path="/admin/forgot-password" element={
            adminToken ? <Navigate to="/admin/dashboard" /> : <AdminForgotPassword />
          } />
          <Route path="/admin/reset-password" element={
            adminToken ? <Navigate to="/admin/dashboard" /> : <AdminResetPassword />
          } />
          <Route path="/admin" element={
            adminToken ? <AdminLayout onLogout={handleAdminLogout} /> : <Navigate to="/admin/login" />
          }>
            <Route index element={<Navigate to="/admin/dashboard" />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="applications" element={<AdminApplications />} />
            <Route path="app-users" element={<AdminAppUsers />} />
            <Route path="plans" element={<AdminPlans />} />
            <Route path="pages" element={<AdminPages />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="support" element={<AdminSupport />} />
            <Route path="registration-requests" element={<AdminRegistrationRequests />} />
            <Route path="billing" element={<AdminBilling />} />
            <Route path="roles" element={<AdminRoles />} />
            <Route path="partners" element={<AdminPartners />} />
            <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
          </Route>
        </Routes>
      </AuthProvider>
    );
  }

  return (
    <AuthProvider>
      <div  >
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
        {/* Main App Routes */}
        {token && onboard ? (
          // If authenticated, show Sidebar, Header, and protected routes



          <div className={dashboardClass}>
            <Sidebar
              key={authUserVersion}
              appId={appId}
              menuItems={menuItems}
              handleRemoveToken={handleRemoveToken}
              handleDashboardClass={handleDashboardClass}
              type={currentSidebar}
              thirdSidebarState={thirdSidebar}
              mobileSidebarOpen={mobileSidebarOpen}
              onMobileSidebarToggle={() => setMobileSidebarOpen((o) => !o)}
              onCloseMobileSidebar={() => setMobileSidebarOpen(false)}
            />

            {mobileSidebarOpen ? (
              <button
                type="button"
                className="dashboard-sidebar-backdrop"
                aria-label="Close menu"
                onClick={() => setMobileSidebarOpen(false)}
              />
            ) : null}

            <div className="dashboard-main-shell">
              <PendingBillBanner />
              <Routes>
                {/* {menuItems.map((item) => (
                      <Route key={item.route} path={`/${item.route}`} element={<item.component />} />
                    ))} */}
                <Route path="/signIn" element={<RedirectAuthenticatedUser />} />
                <Route path="/signup" element={<RedirectAuthenticatedUser />} />
                <Route path="/" element={<Navigate to="/apps" replace />} />
                <Route path="/terms" element={<RedirectToMarketing path="/terms" />} />
                <Route path="/privacy" element={<RedirectToMarketing path="/privacy" />} />
                <Route path="/docs" element={<RedirectToMarketing path="/docs" />} />
                <Route path="/docs/onboarding" element={<RedirectToMarketing path="/docs/onboarding" />} />
                <Route path="/docs/firebase-credentials" element={<RedirectToMarketing path="/docs/firebase-credentials" />} />
                <Route path={`/apps`} element={<RequirePermission anyOf="apps.view"><Applications changeSidebar={handleChangeSidebar} /></RequirePermission>} />
                <Route path={`/apps/:appId`} element={<RequirePermission anyOf="apps.view"><Dashboard handleSetAppId={handleSetAppId} /></RequirePermission>} />
                <Route path={'/testmodal'} element={<TestModal />} />
                <Route path={`/organizations`} element={<RequirePermission anyOf="apps.view"><Organizations /></RequirePermission>} />
                <Route path={`/organizations/new`} element={<RequirePermission anyOf="apps.add"><NewOrganization /></RequirePermission>} />
                <Route path={`/ssl-domain-check`} element={<RequirePermission anyOf="sites.view"><SiteCheck /></RequirePermission>} />
                <Route path={`/apps/:appId/push-notification`} element={<RequirePermission anyOf="push.view"><PushNotification /></RequirePermission>} />
                <Route path={`/apps/:appId/notification-templates`} element={<RequirePermission anyOf="push.view"><PushTemplatesList /></RequirePermission>} />
                <Route path={`/apps/:appId/platform`} element={<RequirePermission anyOf="settings.platform.view"><Platforms changeSidebar={handleChangeSidebar} /></RequirePermission>} />
                <Route path={`/apps/:appId/subscriptions`} element={<RequirePermission anyOf="subscriptions.view"><Subscriptions /></RequirePermission>} />
                <Route path={`/apps/:appId/subscriptions/:subscriptionId`} element={<RequirePermission anyOf="subscriptions.view"><SubscriptionProfile /></RequirePermission>} />
                <Route path={`/apps/:appId/users`} element={<RequirePermission anyOf="app_users.view"><Users /></RequirePermission>} />
                <Route path={`/apps/:appId/users/:userId`} element={<RequirePermission anyOf="app_users.view"><UserProfile /></RequirePermission>} />
                <Route path={`/apps/:appId/testsubscription`} element={<RequirePermission anyOf="test_subscriptions.view"><TestSubscription /></RequirePermission>} />
                <Route path={`/apps/new/:id/setting/configure/:channel_type`} element={<RequirePermission anyOf={["apps.add", "settings.platform.manage", "apps.change_status"]}><AppSetting currentSidebar={currentSidebar} changeSidebar={handleChangeSidebar} /></RequirePermission>} />
                <Route path={`/apps/new`} element={<RequirePermission anyOf="apps.add"><NewApp changeSidebar={handleChangeSidebar} /></RequirePermission>} />
                <Route path={`/apps/:appId/settings/messaging`} element={<RequirePermission anyOf={["settings.messaging.group.view", "settings.messaging.channel.view"]}><PushSetting /></RequirePermission>} />
                <Route path={`/apps/:appId/settings/api-keys`} element={<RequirePermission anyOf="settings.api_keys.view"><ApiKeys /></RequirePermission>} />
                <Route path={`/apps/:appId/settings/app-default-settings`} element={<RequirePermission anyOf={["apps.view", "apps.edit"]}><AppDefaultSetting /></RequirePermission>} />
                <Route path={`/apps/:appId/settings/whatsapp`} element={<WhatsAppLegacyRedirect />} />
                <Route path={`/apps/:appId/whatsapp`} element={<WhatsAppIndexRedirect />} />
                <Route path={`/apps/:appId/whatsapp/campaigns/:campaignId/report`} element={<RequirePermission anyOf="whatsapp.campaigns.view"><WhatsAppCampaignReport /></RequirePermission>} />
                <Route path={`/apps/:appId/whatsapp/:section`} element={<RequirePermission anyOf={["whatsapp.inbox.view", "whatsapp.contacts.view", "whatsapp.campaigns.view", "whatsapp.analytics.view", "whatsapp.business.view", "whatsapp.phones.view", "whatsapp.connection.view", "whatsapp.templates.view", "whatsapp.send.view"]}><WhatsAppSettings /></RequirePermission>} />
                <Route path={`/apps/:appId/support/assignees`} element={<RedirectSupportAssigneesToTeamMembers />} />
                <Route path={`/apps/:appId/support/team-members`} element={<RequirePermission anyOf="support.team_members.view"><AppSupportTeamMembers /></RequirePermission>} />
                <Route path={`/apps/:appId/support/customers`} element={<RequirePermission anyOf="support.customers.view"><AppSupportCustomers /></RequirePermission>} />
                <Route path={`/apps/:appId/support/groups`} element={<RequirePermission anyOf="support.groups.view"><AppSupportGroups /></RequirePermission>} />
                <Route path={`/apps/:appId/support/fields`} element={<RequirePermission anyOf="support.fields.view"><AppSupportFields /></RequirePermission>} />
                <Route path={`/apps/:appId/segments`} element={<RequirePermission anyOf="segments.view"><Segments /></RequirePermission>} />
                <Route path={`/apps/:appId/campaigns`} element={<Navigate to="../engage/campaign" replace />} />
                <Route path={`/apps/:appId/engage/campaign`} element={<RequirePermission anyOf="campaigns.view"><CampaignDashboard /></RequirePermission>} />
                <Route path={`/apps/:appId/engage/campaign/:campaignId`} element={<RequirePermission anyOf="campaigns.view"><CampaignDetail /></RequirePermission>} />
                <Route path={`/apps/:appId/engage/onelink`} element={<RequirePermission anyOf="onelink.view"><OneLinkList /></RequirePermission>} />
                <Route path={`/apps/:appId/engage/onelink/new`} element={<Navigate to="../onelink" replace />} />
                <Route path={`/apps/:appId/engage/event`} element={<RequirePermission anyOf="events.view"><EventList /></RequirePermission>} />
                <Route path={`/apps/:appId/engage/funnel`} element={<RequirePermission anyOf="funnel.view"><FunnelList /></RequirePermission>} />
                <Route path={`/apps/:appId/engage/funnel/add`} element={<RequirePermission anyOf="funnel.manage"><FunnelAnalysis /></RequirePermission>} />
                <Route path={`/apps/:appId/engage/funnel/:funnelId`} element={<RequirePermission anyOf={["funnel.view", "funnel.manage"]}><FunnelAnalysis /></RequirePermission>} />
                <Route path={`/apps/:appId/engage/events`} element={<Navigate to="../event" replace />} />
                <Route path={`/apps/:appId/automations`} element={<RequirePermission anyOf="automation.view"><AutomationsHome /></RequirePermission>} />
                <Route path={`/apps/:appId/automations/:automationId`} element={<RequirePermission anyOf="automation.view"><AutomationBuilder /></RequirePermission>} />
                <Route path={`/apps/:appId/collaborate/partner-integrations`} element={<RequirePermission anyOf={["collaborate.partner_integrations.view","collaborate.partner_integrations.manage"]}><CollaboratePartner /></RequirePermission>} />
                <Route path={`/apps/:appId/collaborate/partner-integrations/:partnerId`} element={<RequirePermission anyOf={["collaborate.partner_integrations.view","collaborate.partner_integrations.manage"]}><CollaboratePartnerDetail /></RequirePermission>} />
                <Route path={`/apps/:appId/sent-messages/:notificationId/report`} element={<RequirePermission anyOf="delivery.sent.view"><SentMessages /></RequirePermission>} />
                <Route path={`/apps/:appId/sent-messages`} element={<RequirePermission anyOf="delivery.sent.view"><SentMessages /></RequirePermission>} />
                <Route path={`/apps/:appId/scheduled-messages`} element={<RequirePermission anyOf="delivery.scheduled.view"><ScheduledMessages /></RequirePermission>} />
                <Route path={`/apps/:appId/support/ticket`} element={<RequirePermission anyOf="support.tickets.view"><AppSupportTicket /></RequirePermission>} />
                <Route path={`/apps/:appId/support/ticket/details/:conversationId`} element={<RequirePermission anyOf="support.tickets.view"><AppSupportTicketDetails /></RequirePermission>} />
                <Route path="/reset-password" element={<RedirectAuthenticatedUser />} />
                <Route path="/forgot-password" element={<RedirectAuthenticatedUser />} />
                <Route path="/plan" element={<RequirePermission anyOf="plan.view"><Plan /></RequirePermission>} />
                <Route path="/payment" element={<RequirePermission anyOf="billing.manage"><Payment /></RequirePermission>} />
                <Route path="/roles" element={<RequirePermission anyOf="roles.view"><Roles /></RequirePermission>} />
                <Route path="/staff" element={<RequirePermission anyOf="team.view"><Staff /></RequirePermission>} />
                <Route path="/team-members" element={<Navigate to="/staff" replace />} />
                <Route path="/plan-change-request" element={<RequirePermission anyOf="billing.manage"><PlanChangeRequest /></RequirePermission>} />
                <Route path="/billing" element={<RequirePermission anyOf="billing.view"><Billing /></RequirePermission>} />
              </Routes>
            </div>
            <SupportChat />
          </div>
        ) : token && !onboard ? (
          <Routes>
            <Route path="/" element={<Navigate to="/steps" replace />} />
            <Route path="/terms" element={<RedirectToMarketing path="/terms" />} />
            <Route path="/privacy" element={<RedirectToMarketing path="/privacy" />} />
            <Route path="/docs" element={<RedirectToMarketing path="/docs" />} />
            <Route path="/docs/onboarding" element={<RedirectToMarketing path="/docs/onboarding" />} />
            <Route path="/docs/firebase-credentials" element={<RedirectToMarketing path="/docs/firebase-credentials" />} />
            <Route path="/steps" element={<OnboardSteps handleSetOnboard={handleSetOnboard} />} />
            <Route path="/plan" element={<Plan />} />
            <Route path="/payment" element={<Payment />} />
            <Route path="/plan-change-request" element={<PlanChangeRequest />} />
            <Route path="/billing" element={<Billing />} />
            <Route path="/signIn" element={<NewSignIn handleSaveToken={handleSaveToken} handleSetOnboard={handleSetOnboard} handleChangeSidebar={handleChangeSidebar} handleSetAppId={handleSetAppId} />} />
            <Route path="/signup" element={<NewUserSignup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="*" element={<Navigate to="/steps" replace />} />
          </Routes>
        ) : (
          <Routes>
            <Route path="/" element={<Navigate to="/signIn" replace />} />
            <Route path="/terms" element={<RedirectToMarketing path="/terms" />} />
            <Route path="/privacy" element={<RedirectToMarketing path="/privacy" />} />
            <Route path="/docs" element={<RedirectToMarketing path="/docs" />} />
            <Route path="/docs/onboarding" element={<RedirectToMarketing path="/docs/onboarding" />} />
            <Route path="/docs/firebase-credentials" element={<RedirectToMarketing path="/docs/firebase-credentials" />} />
            <Route path="/signIn" element={<NewSignIn handleSaveToken={handleSaveToken} handleSetOnboard={handleSetOnboard} handleChangeSidebar={handleChangeSidebar} handleSetAppId={handleSetAppId} />} />
            {/* New signup form that collects all user data for admin approval */}
            <Route path="/signup" element={<NewUserSignup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="*" element={<RedirectUnauthenticatedToSignIn />} />
          </Routes>
        )}
      </div>
    </AuthProvider>
  );
};


// <Routes>
// { token && !isPlan ? <Route path="/plan" element={<Plan handleSetOnboard={handleSetOnboard} />} />:""}
// </Routes>
export default App;
